// @ts-check
import React from 'react';

import usePackageDetails from 'modules/queries/market/package/useDetails';
import AvailableAddOnCard from './AvailableAddOnCard';

/**
 * @typedef HookedAvailableAddOnRequiredProps
 * @prop {string} code
 * @prop {() => void} onAddClick
 * @prop {number} params.regionId the region of the subaccnt that the add-on will be added to
 * */

/**
 * @param {string | number} discountPercent
 * @param {number | null} [discountRepetitions]
 * @returns {string}
 */
export const createDiscountString = (discountPercent, discountRepetitions) => {
	const percent = Number(discountPercent);
	if (!percent) {
		return '';
	}

	if (!discountRepetitions) {
		return `${Number(percent.toFixed(2))}% off`;
	}

	return `${Number(percent.toFixed(2))}% off for ${discountRepetitions} months`;
};

/**
 * @param {HookedAvailableAddOnRequiredProps & Partial<import('react').ComponentProps<typeof AvailableAddOnCard>>} props
 */
const HookedAvailableAddOnCard = ({
	code,
	title: titleProp,
	description: descriptionProp,
	price: priceProp,
	discount: discountProp,
	regionId,
	...rest
}) => {
	const { data: packageDetails, isLoading } = usePackageDetails({
		code,
		alsowith: ['defaultPriceByCycle', 'products'],
	});

	const isAvailableInRegion = packageDetails?.version.products?.every(
		(product) =>
			product.region_id_availability?.some(
				(addOnRegion) => addOnRegion.toString() === regionId.toString(),
			),
	);

	if (isAvailableInRegion === false) {
		return null;
	}

	const title = titleProp || packageDetails?.title || '';
	const description =
		descriptionProp || packageDetails?.version.description || '';
	const price =
		priceProp || packageDetails?.version.formattedBestMonthlyDeal || '';
	const discountPercent =
		packageDetails?.version.mostDiscountedCycle?.discount_percent || '';
	const discountRepetitions =
		packageDetails?.version.mostDiscountedCycle?.discount_cycle_repetitions;
	const discount =
		discountProp || createDiscountString(discountPercent, discountRepetitions);

	return (
		<AvailableAddOnCard
			code={code}
			title={title}
			description={description}
			price={price}
			isLoading={isLoading}
			discount={discount}
			{...rest}
		/>
	);
};

export default HookedAvailableAddOnCard;
